import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Download, Copy, Search, Loader2 } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { characters } from "@/lib/characters";

// Alias map: tag string -> all related aliases (en/zh/ja) for search matching
const CHARACTER_ALIASES: Record<string, string[]> = {};
for (const c of characters) {
  const all = [c.name, c.nameZh, c.nameJa].map((s) => s.toLowerCase());
  for (const key of [c.name, c.nameZh, c.nameJa]) {
    CHARACTER_ALIASES[key.toLowerCase()] = all;
  }
}


interface Category {
  id: string;
  name: string;
  slug: string;
}
interface ImageRow {
  id: string;
  category_id: string;
  file_name: string;
  original_name: string;
  public_url: string;
  storage_path: string;
  tags: string[];
  audience: string[];
}

const BUCKET = "image-library";
const SIGNED_TTL = 60 * 60;

const CHECKER_STYLE = {
  backgroundImage:
    "linear-gradient(45deg, hsl(var(--muted)) 25%, transparent 25%), linear-gradient(-45deg, hsl(var(--muted)) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, hsl(var(--muted)) 75%), linear-gradient(-45deg, transparent 75%, hsl(var(--muted)) 75%)",
  backgroundSize: "18px 18px",
  backgroundPosition: "0 0, 0 9px, 9px -9px, -9px 0",
} as const;

const AUDIENCES = [
  { key: "all", label: "全部" },
  { key: "parents", label: "家長" },
  { key: "teachers", label: "老師" },
];

const ImageLibrary = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [images, setImages] = useState<ImageRow[]>([]);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [activeCat, setActiveCat] = useState<string>("all");
  const [activeCharacter, setActiveCharacter] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [audience, setAudience] = useState<string>("all");


  useEffect(() => {
    (async () => {
      const [cRes, iRes] = await Promise.all([
        supabase.from("categories").select("*").order("sort_order"),
        supabase.from("images").select("*").eq("is_active", true).order("file_name"),
      ]);
      if (cRes.data) setCategories(cRes.data);
      if (iRes.data) {
        setImages(iRes.data);
        const paths = iRes.data.map((r) => r.storage_path);
        if (paths.length) {
          const { data } = await supabase.storage
            .from(BUCKET)
            .createSignedUrls(paths, SIGNED_TTL);
          const m: Record<string, string> = {};
          (data ?? []).forEach((d, i) => {
            if (d.signedUrl) m[iRes.data![i].id] = d.signedUrl;
          });
          setSignedUrls(m);
        }
      }
    })();
  }, []);

  const audienceFiltered = useMemo(
    () =>
      audience === "all"
        ? images
        : images.filter((i) => i.audience.includes(audience)),
    [images, audience]
  );

  const catCounts = useMemo(() => {
    const m: Record<string, number> = { all: audienceFiltered.length };
    for (const img of audienceFiltered) m[img.category_id] = (m[img.category_id] ?? 0) + 1;
    return m;
  }, [audienceFiltered]);

  const catNameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const c of categories) m[c.id] = c.name;
    return m;
  }, [categories]);

  const charCounts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const ch of characters) {
      m[ch.name] = audienceFiltered.filter(
        (i) => i.tags.includes(ch.name) || i.tags.includes(ch.nameZh) || i.tags.includes(ch.nameJa)
      ).length;
    }
    return m;
  }, [audienceFiltered]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return audienceFiltered.filter((img) => {
      if (activeCat !== "all" && img.category_id !== activeCat) return false;
      if (activeCharacter) {
        const ch = characters.find((c) => c.name === activeCharacter);
        if (!ch) return false;
        if (
          !img.tags.includes(ch.name) &&
          !img.tags.includes(ch.nameZh) &&
          !img.tags.includes(ch.nameJa)
        )
          return false;
      }
      if (!q) return true;
      const catName = (catNameById[img.category_id] ?? "").toLowerCase();
      // Expand tags with character aliases so 中/英/日 all match
      const expandedTags = new Set<string>();
      for (const t of img.tags) {
        const lower = t.toLowerCase();
        expandedTags.add(lower);
        const aliases = CHARACTER_ALIASES[lower];
        if (aliases) aliases.forEach((a) => expandedTags.add(a));
      }
      return (
        img.file_name.toLowerCase().includes(q) ||
        (img.original_name ?? "").toLowerCase().includes(q) ||
        catName.includes(q) ||
        Array.from(expandedTags).some((t) => t.includes(q))
      );
    });
  }, [audienceFiltered, activeCat, activeCharacter, query, catNameById]);


  const download = async (img: ImageRow) => {
    trackEvent("library_image_download", "library", img.file_name);
    const { data, error } = await supabase.storage.from(BUCKET).download(img.storage_path);
    if (error || !data) return toast.error("下載失敗");
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = img.file_name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const copyUrl = async (img: ImageRow) => {
    trackEvent("library_image_url_copy", "library", img.file_name);
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(img.storage_path, 60 * 60 * 24 * 30);
    if (error) return toast.error(error.message);
    await navigator.clipboard.writeText(data.signedUrl);
    toast.success("已複製圖片連結（有效 30 天）");
  };

  return (
    <>
      <Helmet>
        <title>Queebi 圖庫</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <main className="min-h-screen bg-background">
        <header className="border-b border-border bg-gradient-to-b from-accent/10 to-background">
          <div className="container py-8 space-y-4">
            <div>
              <h1 className="font-heading text-3xl font-bold">Queebi 圖庫</h1>
              <p className="text-sm text-muted-foreground mt-1">
                提供家長與幼兒園老師下載使用（單張最大 1000×1000）。
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="搜尋名稱、分類或標籤…"
                  className="pl-9 h-11"
                />
              </div>
              <div className="flex gap-2">
                {AUDIENCES.map((a) => (
                  <button
                    key={a.key}
                    onClick={() => setAudience(a.key)}
                    className={`px-4 py-2 text-sm rounded-full border font-medium transition-colors ${
                      audience === a.key
                        ? "bg-foreground text-background border-foreground"
                        : "bg-background border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </header>

        <div className="container py-6 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
          <aside className="lg:sticky lg:top-6 lg:self-start space-y-1">
            <div className="text-xs uppercase text-muted-foreground font-semibold mb-2 px-3">
              分類
            </div>
            <button
              onClick={() => {
                setActiveCat("all");
                setActiveCharacter(null);
              }}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                activeCat === "all" && !activeCharacter
                  ? "bg-accent/20 text-foreground font-medium"
                  : "hover:bg-muted text-muted-foreground"
              }`}
            >
              全部 ({catCounts.all ?? 0})
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => {
                  setActiveCat(c.id);
                  setActiveCharacter(null);
                }}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeCat === c.id && !activeCharacter
                    ? "bg-accent/20 text-foreground font-medium"
                    : "hover:bg-muted text-muted-foreground"
                }`}
              >
                {c.name} ({catCounts[c.id] ?? 0})
              </button>
            ))}

            <div className="text-xs uppercase text-muted-foreground font-semibold mb-2 mt-6 px-3">
              角色
            </div>
            {characters.map((ch) => (
              <button
                key={ch.slug}
                onClick={() => {
                  setActiveCharacter(activeCharacter === ch.name ? null : ch.name);
                  setActiveCat("all");
                }}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeCharacter === ch.name
                    ? "bg-accent/20 text-foreground font-medium"
                    : "hover:bg-muted text-muted-foreground"
                }`}
              >
                {ch.nameZh} ({charCounts[ch.name] ?? 0})
              </button>
            ))}
          </aside>


          <section>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {filtered.map((img) => {
                const src = signedUrls[img.id];
                return (
                  <article
                    key={img.id}
                    className="bg-card border border-border rounded-xl overflow-hidden group shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="relative aspect-square" style={CHECKER_STYLE}>
                      {src ? (
                        <img
                          src={src}
                          alt={img.original_name || img.tags.join(", ") || img.file_name}
                          loading="lazy"
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-foreground/70 opacity-0 group-hover:opacity-100 transition-opacity p-3 flex flex-col justify-end text-background text-xs space-y-1">
                        <div className="font-medium">{catNameById[img.category_id]}</div>
                        <div className="font-mono opacity-80">{img.file_name}</div>
                        {img.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {img.tags.slice(0, 5).map((t) => (
                              <span
                                key={t}
                                className="bg-background/20 px-1.5 py-0.5 rounded text-[10px]"
                              >
                                {t}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="p-3 space-y-2">
                      <div className="text-xs truncate" title={img.original_name}>
                        {img.original_name}
                      </div>
                      <div className="text-[10px] font-mono text-muted-foreground truncate">
                        {img.file_name}
                      </div>
                      {img.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {img.tags.slice(0, 4).map((t) => (
                            <Badge key={t} variant="secondary" className="text-[10px]">
                              {t}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-1 pt-1">
                        <Button
                          size="sm"
                          onClick={() => download(img)}
                          className="flex-1 h-8 text-xs rounded-full"
                        >
                          <Download className="h-3 w-3 mr-1" /> 下載
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyUrl(img)}
                          className="flex-1 h-8 text-xs rounded-full"
                        >
                          <Copy className="h-3 w-3 mr-1" /> 連結
                        </Button>
                      </div>
                    </div>
                  </article>
                );
              })}
              {filtered.length === 0 && (
                <div className="col-span-full text-center text-sm text-muted-foreground py-16">
                  找不到符合的圖片。
                </div>
              )}
            </div>
          </section>
        </div>
      </main>
    </>
  );
};

export default ImageLibrary;
